import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Editor from "@monaco-editor/react";
import { useState } from "react";
import type { MicroAppVersion } from "../../../api/client.ts";

interface CodeTab {
  label: string;
  language: string;
  value: string;
}

/** Read-only Monaco viewer for a published micro app version's manifest/code. */
function MicroAppCodeViewerDialog({
  version,
  onClose,
}: {
  version: MicroAppVersion | null;
  onClose: () => void;
}) {
  const theme = useTheme();
  const [tabIndex, setTabIndex] = useState(0);

  if (!version) return null;

  const tabs: CodeTab[] = [
    { label: "Manifest", language: "json", value: JSON.stringify(version.manifest, null, 2) },
    ...(version.client_code
      ? [{ label: "Client code", language: "html", value: version.client_code }]
      : []),
    ...(version.server_code
      ? [{ label: "Server code", language: "javascript", value: version.server_code }]
      : []),
  ];
  const active = tabs[Math.min(tabIndex, tabs.length - 1)];

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Code: v{version.version}
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Tabs
          value={tabs.indexOf(active)}
          onChange={(_, next) => setTabIndex(next)}
          sx={{ px: 2, borderBottom: 1, borderColor: "divider" }}
        >
          {tabs.map((t) => (
            <Tab key={t.label} label={t.label} />
          ))}
        </Tabs>
        <Box sx={{ height: 480 }}>
          <Editor
            key={active.label}
            language={active.language}
            value={active.value}
            theme={theme.palette.mode === "dark" ? "vs-dark" : "light"}
            options={{ readOnly: true, minimap: { enabled: false }, fontSize: 13 }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default MicroAppCodeViewerDialog;
